/**
 * The page's one bold moment: an abstract node-and-edge graph drawn behind
 * the title of an index page. It draws itself once when the page opens and
 * then holds still. Positions are seeded, so it is the same picture on every
 * visit; colours are read from the theme tokens at draw time, so it follows
 * the theme toggle without a reload.
 *
 * Purely decorative — no data, no labels — and hidden from assistive tech.
 */

import { useEffect, useRef } from 'react'

type Node = { x: number; y: number; r: number }

function seeded(seed: number) {
  let s = seed
  return () => {
    s = (s * 16807) % 2147483647
    return (s - 1) / 2147483646
  }
}

function build(w: number, h: number): { nodes: Node[]; edges: [number, number][] } {
  const rnd = seeded(7)
  const count = Math.max(14, Math.min(34, Math.round(w / 40)))
  const nodes: Node[] = []
  for (let i = 0; i < count; i++) {
    nodes.push({
      // Kept to the right of the title, which the gradient below protects.
      x: w * (0.44 + rnd() * 0.54),
      y: h * (0.08 + rnd() * 0.84),
      r: 1.5 + rnd() * 2.2,
    })
  }
  const edges: [number, number][] = []
  for (let a = 0; a < count; a++) {
    const near = nodes
      .map((n, b) => ({ b, d: Math.hypot(nodes[a].x - n.x, nodes[a].y - n.y) }))
      .filter((e) => e.b !== a)
      .sort((p, q) => p.d - q.d)
    for (let k = 0; k < 2; k++) {
      const e = near[k]
      if (e && a < e.b) edges.push([a, e.b])
    }
  }
  return { nodes, edges }
}

function tokens() {
  const s = getComputedStyle(document.documentElement)
  return {
    accent: s.getPropertyValue('--accent').trim(),
    muted: s.getPropertyValue('--border-strong').trim(),
    surface: s.getPropertyValue('--surface').trim(),
  }
}

export function Constellation() {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let raf = 0
    let graph = build(1, 1)

    function render(t: number) {
      if (!canvas || !ctx) return
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      const c = tokens()
      ctx.clearRect(0, 0, w, h)
      ctx.lineWidth = 1
      graph.edges.forEach(([ai, bi], i) => {
        const p = Math.min(1, Math.max(0, t * graph.edges.length - i * 0.6))
        if (p <= 0) return
        const a = graph.nodes[ai]
        const b = graph.nodes[bi]
        ctx.strokeStyle = c.muted
        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        ctx.lineTo(a.x + (b.x - a.x) * p, a.y + (b.y - a.y) * p)
        ctx.stroke()
      })
      graph.nodes.forEach((n, i) => {
        const p = Math.min(1, Math.max(0, t * 1.4 - i / graph.nodes.length))
        if (p <= 0) return
        ctx.fillStyle = i % 5 === 0 ? c.accent : c.muted
        ctx.beginPath()
        ctx.arc(n.x, n.y, n.r * p, 0, Math.PI * 2)
        ctx.fill()
      })
      // Fade toward the copy on the left so the title always reads.
      const g = ctx.createLinearGradient(0, 0, w, 0)
      g.addColorStop(0, c.surface)
      g.addColorStop(0.42, c.surface)
      g.addColorStop(0.68, `${c.surface}00`)
      g.addColorStop(1, `${c.surface}00`)
      ctx.fillStyle = g
      ctx.fillRect(0, 0, w, h)
    }

    function draw(animate: boolean) {
      if (!canvas || !ctx) return
      const dpr = window.devicePixelRatio || 1
      const w = canvas.clientWidth
      const h = canvas.clientHeight
      if (!w || !h) return
      canvas.width = w * dpr
      canvas.height = h * dpr
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      graph = build(w, h)
      cancelAnimationFrame(raf)
      if (!animate || reduced) {
        render(1)
        return
      }
      const start = performance.now()
      const dur = 1400
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / dur)
        render(1 - Math.pow(1 - t, 3))
        if (t < 1) raf = requestAnimationFrame(step)
      }
      raf = requestAnimationFrame(step)
    }

    draw(true)

    const ro = new ResizeObserver(() => draw(false))
    ro.observe(canvas)
    // The theme toggle stamps data-theme on <html>; redraw in the new colours.
    const mo = new MutationObserver(() => draw(false))
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      mo.disconnect()
    }
  }, [])

  return <canvas ref={ref} className="constellation" aria-hidden="true" />
}
